import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Select, Switch, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { createSupportTicket, TICKET_CATEGORIES } from '../modules/support-tickets/api';
import { getPartners } from '../modules/partners/api';
import { getOrders } from '../modules/orders/api';
import TicketLinkFields from './TicketLinkFields';

/**
 * OVO-117 — shared admin "Create ticket" modal. Every operator surface uses it
 * with a different prefill: OrderDetail passes the order (whose id derives
 * partner/route/driver server-side), RouteDetail the route, PartnerDetail the
 * partner, the Tickets page nothing. When no order is prefilled, an optional
 * order-code field resolves to an id at submit time.
 * #132 — without an order / box / site / schedule context (Tickets page, route,
 * partner) the operator can pick the box, site or recurring schedule here.
 */
export default function CreateTicketModal({ open, onClose, onCreated, prefill = {} }) {
  const { t } = useTranslation(['routes', 'common']);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  const hasOrderContext = !!prefill.orderId;
  const showLinkFields = !hasOrderContext && !prefill.collectionPointId && !prefill.siteId && !prefill.orderScheduleId;
  const watchedPartnerId = Form.useWatch('partnerId', form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      category: prefill.category || 'GENERAL',
      subject: prefill.subject || '',
      partnerId: prefill.partnerId,
      internal: prefill.internal ?? false,
    });
    if (!prefill.partnerId) {
      getPartners({ take: 300 }).then(({ response }) => {
        setPartnerOptions((response?.partners || []).map((p) => ({ value: p.id, label: p.name })));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      let orderId = prefill.orderId;
      if (!orderId && values.orderCode) {
        const { response } = await getOrders({ q: values.orderCode.trim(), take: 5 });
        const match = (response?.orders || []).find(
          (o) => o.orderCode?.toLowerCase() === values.orderCode.trim().toLowerCase(),
        );
        if (!match) {
          messageApi.error(t('tickets.orderNotFound', { code: values.orderCode }));
          setSaving(false);
          return;
        }
        orderId = match.id;
      }
      const { response } = await createSupportTicket({
        subject: values.subject,
        category: values.category,
        description: values.description || undefined,
        internal: values.internal ?? false,
        orderId: orderId || undefined,
        routeId: prefill.routeId || undefined,
        driverId: prefill.driverId || undefined,
        siteId: prefill.siteId || values.siteId || undefined,
        collectionPointId: prefill.collectionPointId || values.collectionPointId || undefined,
        orderScheduleId: prefill.orderScheduleId || values.orderScheduleId || undefined,
        partnerId: values.partnerId || prefill.partnerId || undefined,
      });
      if (response?.id) {
        messageApi.success(t('tickets.created', { id: response.id }));
        onCreated?.(response);
        onClose();
      } else {
        messageApi.error(response?.message || t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t('tickets.createTitle')}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText={t('tickets.create')}
      confirmLoading={saving}
      destroyOnClose
    >
      {contextHolder}
      <Form form={form} layout="vertical">
        {hasOrderContext && (
          <div style={{ marginBottom: 12, color: 'var(--text-secondary)' }}>
            {t('tickets.linkedToOrder', { code: prefill.orderCode || `#${prefill.orderId}` })}
          </div>
        )}
        {/* #132 — a box, a site or a recurring schedule */}
        {prefill.collectionPointId && (
          <div style={{ marginBottom: 12, color: 'var(--text-secondary)' }}>
            {t('tickets.linkedToBox', { name: prefill.collectionPointLabel || `#${prefill.collectionPointId}` })}
          </div>
        )}
        {prefill.siteId && !prefill.collectionPointId && (
          <div style={{ marginBottom: 12, color: 'var(--text-secondary)' }}>
            {t('tickets.linkedToSite', { name: prefill.siteLabel || `#${prefill.siteId}` })}
          </div>
        )}
        {prefill.orderScheduleId && (
          <div style={{ marginBottom: 12, color: 'var(--text-secondary)' }}>
            {t('tickets.linkedToSchedule', { id: prefill.orderScheduleId })}
          </div>
        )}
        {prefill.routeId && !hasOrderContext && (
          <div style={{ marginBottom: 12, color: 'var(--text-secondary)' }}>
            {t('tickets.linkedToRoute', { id: prefill.routeId })}
          </div>
        )}
        <Form.Item
          name="subject"
          label={t('tickets.subject')}
          rules={[{ required: true, message: t('tickets.subjectRequired') }]}
        >
          <Input maxLength={200} placeholder={t('tickets.subjectPlaceholder')} />
        </Form.Item>
        <Form.Item name="category" label={t('tickets.category')} rules={[{ required: true }]}>
          <Select
            options={TICKET_CATEGORIES.map((c) => ({
              value: c,
              label: t(`tickets.categories.${c}`, c),
            }))}
          />
        </Form.Item>
        <Form.Item name="description" label={t('tickets.description')}>
          <Input.TextArea rows={4} maxLength={5000} />
        </Form.Item>
        {!prefill.partnerId && !hasOrderContext && (
          <Form.Item name="partnerId" label={t('tickets.partner')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={partnerOptions}
              placeholder={t('tickets.partnerOptional')}
            />
          </Form.Item>
        )}
        {showLinkFields && <TicketLinkFields form={form} partnerId={prefill.partnerId || watchedPartnerId} />}
        {!hasOrderContext && (
          <Form.Item name="orderCode" label={t('tickets.orderCodeOptional')}>
            <Input placeholder="PXXXXXXXX" />
          </Form.Item>
        )}
        <Form.Item
          name="internal"
          label={t('tickets.internal')}
          valuePropName="checked"
          tooltip={t('tickets.internalHint')}
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
